/**
 * Role-color mapping composable. Centralises the three role-tinted CSS
 * custom properties (`--color-role-{tank,dps,heal}`) so any component that
 * needs the bg / border / foreground pair can ask for them rather than
 * re-declaring its own `Readonly<Record<WeaponRoleColor, …>>` map.
 *
 * The Tailwind `@theme` block in `src/styles/tailwind.css` is the source of
 * truth for the actual hex values; this composable only references those
 * tokens (via the `var(--color-role-*)` strings or the Tailwind class
 * counterparts that the JIT scanner emits from the same `@theme` block).
 */
import { WeaponRoleColor } from '@/types/weapon'
import type { WeaponRole } from '@/types/weapon'
import { roleToColor } from '@/types/weapon'

export interface RoleColorSet {
  /** Tailwind background utility (e.g. `bg-role-tank`). */
  readonly bg: string
  /** Raw CSS custom property (e.g. `var(--color-role-tank)`). */
  readonly borderVar: string
}

export interface RoleColorsApi {
  /** Maps a `WeaponRoleColor` to its bg class + border custom property. */
  readonly byColor: Readonly<Record<WeaponRoleColor, RoleColorSet>>
  /** Resolve through `roleToColor` first — handy for `Weapon.role` lookups. */
  forRole(role: WeaponRole): RoleColorSet
}

/**
 * Shared role-color map. Allocated once at module load so every caller of
 * `useRoleColors` receives the same object identity — no per-component cost
 * and stable references for Vue's reactivity diffing.
 */
const BY_COLOR: Readonly<Record<WeaponRoleColor, RoleColorSet>> = {
  [WeaponRoleColor.Tank]: {
    bg: 'bg-role-tank',
    borderVar: 'var(--color-role-tank)',
  },
  [WeaponRoleColor.Dps]: {
    bg: 'bg-role-dps',
    borderVar: 'var(--color-role-dps)',
  },
  [WeaponRoleColor.Heal]: {
    bg: 'bg-role-heal',
    borderVar: 'var(--color-role-heal)',
  },
}

export function useRoleColors(): RoleColorsApi {
  return {
    byColor: BY_COLOR,
    forRole(role: WeaponRole): RoleColorSet {
      return BY_COLOR[roleToColor(role)]
    },
  }
}